// ServiceOS/frontend/src/components/forms/FormRenderer.js

import React, { useState, useCallback, useEffect, Fragment, useRef, forwardRef, useImperativeHandle, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Autocomplete } from '@react-google-maps/api';
import ModernInput from '../common/ModernInput';
import ModernSelect from '../common/ModernSelect';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import SignatureCanvas from 'react-signature-canvas';
import { PlusIcon, TrashIcon, XCircleIcon, ArrowRightCircleIcon, CheckCircleIcon, StarIcon, DocumentTextIcon } from '@heroicons/react/20/solid';
import { CheckCircle2, AlertTriangle, Camera, Image as ImageIcon, X, MousePointerClick, Check, Copy, Pencil, Trash2, Ban } from 'lucide-react'; 
import { useCurrency } from '../context/CurrencyContext';
import { useSettings } from '../context/SettingsContext'; 
import StockItemsField from './StockItemsField';
import { MapsApiContext } from '../../App';

// ⭐ NATIVE IMPORTS
import { Capacitor } from '@capacitor/core';
import { Camera as NativeCamera, CameraResultType, CameraSource } from '@capacitor/camera';

const UK_BOUNDS = {
    north: 60.86,
    south: 49.88,
    west: -8.65,
    east: 1.77,
};

const defaultGlobalStyles = {
    backgroundColor: '#FFFFFF',
    primaryColor: '#2563EB',
    borderColor: '#D1D5DB',
    labelColor: '#111827',
    logoUrl: '',
    hideLogo: false,
    borderRadius: '0.375rem',
    globalBorderWidth: 1,
    globalBorderStyle: 'solid',
};

const defaultFieldStyles = {
    labelColor: '#111827',
    inputTextColor: '#111827',
    inputBackgroundColor: '#FFFFFF',
    inputBorderColor: '#D1D5DB',
    inputBorderRadius: '0.375rem',
    inputBorderWidth: 1,
    inputBorderStyle: 'solid',
};

// Star Rating Input Component
const StarRatingInput = ({ name, value, onChange, totalStars = 5, required, error }) => {
    const [hoverRating, setHoverRating] = useState(0);

    const handleStarClick = (rating) => {
        onChange({ target: { name, value: rating } });
    };

    return (
        <div>
            <div className="flex items-center space-x-1">
                {[...Array(totalStars)].map((_, index) => {
                    const ratingValue = index + 1;
                    return (
                        <StarIcon
                            key={ratingValue}
                            className={`h-8 w-8 cursor-pointer transition-colors duration-150 ${
                                ratingValue <= (hoverRating || value)
                                    ? 'text-yellow-400'
                                    : 'text-gray-300 dark:text-gray-600'
                            }`}
                            onClick={() => handleStarClick(ratingValue)}
                            onMouseEnter={() => setHoverRating(ratingValue)}
                            onMouseLeave={() => setHoverRating(0)}
                        />
                    );
                })}
            </div>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
    );
};

// ⭐ Area Assessment Input Component
const AreaAssessmentInput = ({ field, value, onChange, labelStyle, isPreview, error, jobId, photoUploadUrl = null }) => {
    // ⭐ NEW: Where photos upload to straight away. On a job it's the job's photo endpoint
    // (unchanged). Without a job (e.g. a standalone Site Assessment) the caller can pass
    // photoUploadUrl instead, so photos still upload immediately and attach to the right
    // area/item. If neither is set, behaviour is exactly as before (held until submit).
    const uploadUrl = jobId ? `/jobs/${jobId}/photos` : (photoUploadUrl || null);
    const webInputRef = useRef(null);
    const [activePhoto, setActivePhoto] = useState(null); // { roomId, photoIndex, url }
    // ⭐ NEW: Which area's name is being edited, and which copy is waiting for a second
    // tap to confirm removal (avoids native confirm() dialogs, which aren't reliable in
    // the mobile app's webview).
    const [editingNameId, setEditingNameId] = useState(null);
    const [confirmRemoveId, setConfirmRemoveId] = useState(null);

    // ⭐ NEW: new-format fields define nested checklist areas (field.areas); legacy fields
    // only have a flat list of area/room names (field.options). Presence of field.areas
    // decides which mode this field renders in.
    const isChecklistMode = Array.isArray(field.areas) && field.areas.length > 0;

    const rooms = useMemo(() => {
        if (Array.isArray(value) && value.length > 0) return value;

        if (isChecklistMode) {
            return field.areas.map(area => ({
                id: area.id,
                name: area.name,
                photos: [],
                previewUrls: [],
                annotations: {},
                notes: {},
                items: (area.items || []).map(item => ({
                    id: item.id,
                    text: item.text,
                    status: null, // null | 'green' | 'yellow' | 'red' | 'na'
                    comment: '',
                    photos: [],
                    previewUrls: [],
                })),
            }));
        }

        // Legacy: flat room-name list, single good/bad status per room
        return (field.options || []).map(opt => ({
            id: String(opt).toLowerCase().replace(/[^a-z0-9]/g, '_'),
            name: String(opt),
            status: null,
            photos: [],
            previewUrls: [],
            annotations: {}, 
            notes: {} 
        }));
    }, [value, field.options, field.areas, isChecklistMode]);

    const updateRoom = (roomId, updates) => {
        const newRooms = rooms.map(r => r.id === roomId ? { ...r, ...updates } : r);
        onChange({ target: { name: field.name, value: newRooms } });
    };

    const setRooms = (newRooms) => {
        onChange({ target: { name: field.name, value: newRooms } });
    };

    // ⭐ NEW: Every area in an audit is either one of the form's own areas (its id is the
    // template area's id) or a copy the supervisor added on site (sourceAreaId points back
    // at the template area it was copied from).
    const getTemplateArea = (room) => (field.areas || []).find(a => a.id === (room.sourceAreaId || room.id)) || null;
    const canDuplicateRoom = (room) => !!getTemplateArea(room)?.allowDuplicate;

    // ⭐ NEW: Mark an area as "Not required" (e.g. no reception at this site). Its items are
    // hidden and it's left out of the score. Any ratings already entered are kept, so
    // switching it back on doesn't lose them.
    const toggleNotRequired = (e, roomId) => {
        e.preventDefault();
        e.stopPropagation();
        if (isPreview) return;
        const room = rooms.find(r => r.id === roomId);
        updateRoom(roomId, { notRequired: !room?.notRequired });
    };

    // ⭐ NEW: Add another copy of an area (e.g. a second office). The copy gets a brand new
    // id - the backend matches uploaded photos to the right area/item by these ids, so a
    // copy must never share its id with another area. Item ids are kept from the form so
    // the same checklist line can be compared across copies and across audits.
    const duplicateRoom = (e, roomId) => {
        e.preventDefault();
        e.stopPropagation();
        if (isPreview) return;
        const room = rooms.find(r => r.id === roomId);
        if (!room) return;
        const template = getTemplateArea(room);
        const sourceId = room.sourceAreaId || room.id;
        const baseName = (template?.name || room.name || 'Area').trim();

        const siblingIndexes = rooms
            .map((r, idx) => ((r.sourceAreaId || r.id) === sourceId ? idx : -1))
            .filter(idx => idx !== -1);
        const existingNames = new Set(rooms.map(r => (r.name || '').trim().toLowerCase()));

        let n = siblingIndexes.length + 1;
        let newName = `${baseName} ${n}`;
        while (existingNames.has(newName.toLowerCase())) {
            n += 1;
            newName = `${baseName} ${n}`;
        }

        const sourceItems = (template?.items && template.items.length > 0) ? template.items : (room.items || []);
        const newRoom = {
            id: `${sourceId}__copy_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
            sourceAreaId: sourceId,
            isDuplicate: true,
            name: newName,
            notRequired: false,
            photos: [],
            previewUrls: [],
            annotations: {},
            notes: {},
            items: sourceItems.map(item => ({
                id: item.id,
                text: item.text,
                status: null,
                comment: '',
                photos: [],
                previewUrls: [],
            })),
        };

        let newRooms = [...rooms];
        // First copy of an area: number the original too ("Office" -> "Office 1") so the
        // per-area scores are easy to tell apart on the results and PDF.
        if (siblingIndexes.length === 1) {
            const originalIdx = siblingIndexes[0];
            if ((newRooms[originalIdx].name || '').trim() === baseName) {
                newRooms[originalIdx] = { ...newRooms[originalIdx], name: `${baseName} 1` };
            }
        }
        const insertAt = siblingIndexes[siblingIndexes.length - 1] + 1;
        newRooms.splice(insertAt, 0, newRoom);
        setRooms(newRooms);
        setEditingNameId(newRoom.id);
    };

    // ⭐ NEW: Remove a copy that was added on site. Only copies can be removed - the form's
    // own areas can only be marked Not required. If anything has been rated or photographed
    // in the copy, a second tap is needed to confirm.
    const removeRoom = (e, roomId) => {
        e.preventDefault();
        e.stopPropagation();
        if (isPreview) return;
        const room = rooms.find(r => r.id === roomId);
        if (!room || !room.isDuplicate) return;
        const hasWork = (room.items || []).some(i => i.status || (i.photos && i.photos.length > 0) || (i.comment && i.comment.trim()))
            || (room.photos && room.photos.length > 0);
        if (hasWork && confirmRemoveId !== roomId) {
            setConfirmRemoveId(roomId);
            return;
        }
        setConfirmRemoveId(null);
        setRooms(rooms.filter(r => r.id !== roomId));
    };

    const renameRoom = (roomId, name) => {
        updateRoom(roomId, { name });
    };

    // ⭐ NEW: Live scoring, using the same points as the results screen. While the audit is
    // being filled in, the percentage is worked out from the items rated so far.
    const scoringCfg = field.scoring || {};
    const pointsFor = (status) => {
        if (status === 'green') return scoringCfg.greenPoints ?? 10;
        if (status === 'yellow') return scoringCfg.yellowPoints ?? 5;
        if (status === 'red') return scoringCfg.redPoints ?? 0;
        return 0;
    };
    const maxPerItem = Math.max(scoringCfg.greenPoints ?? 10, scoringCfg.yellowPoints ?? 5, scoringCfg.redPoints ?? 0);
    // ⭐ NEW: N/A on single checklist items (on unless the form switches it off). An N/A
    // item counts as answered but is left out of the score.
    const allowNa = scoringCfg.allowNa !== false;
    const scoreRoom = (room) => {
        const items = room.items || [];
        const rated = items.filter(i => i.status);
        const scored = rated.filter(i => i.status !== 'na');
        const points = scored.reduce((sum, i) => sum + pointsFor(i.status), 0);
        const max = scored.length * maxPerItem;
        return { total: items.length, rated: rated.length, points, max, pct: max > 0 ? (points / max) * 100 : null };
    };

    const cycleStatus = (e, roomId, currentStatus) => {
        e.preventDefault();
        e.stopPropagation();
        if (isPreview) return;
        
        const nextStatus = currentStatus === null ? 'good' : currentStatus === 'good' ? 'bad' : null;
        updateRoom(roomId, { 
            status: nextStatus, 
            photos: nextStatus === null ? [] : rooms.find(r => r.id === roomId).photos,
            annotations: nextStatus === null ? {} : rooms.find(r => r.id === roomId).annotations,
            notes: nextStatus === null ? {} : rooms.find(r => r.id === roomId).notes
        });
    };

    // ⭐ NEW: Cycle a single checklist item through null -> green -> yellow -> red -> N/A -> null
    // (N/A is skipped when the form has it switched off)
    const cycleItemStatus = (e, roomId, itemId, currentStatus) => {
        e.preventDefault();
        e.stopPropagation();
        if (isPreview) return;

        const nextStatus = !currentStatus ? 'green'
            : currentStatus === 'green' ? 'yellow'
            : currentStatus === 'yellow' ? 'red'
            : (currentStatus === 'red' && allowNa) ? 'na'
            : null;
        const room = rooms.find(r => r.id === roomId);
        const newItems = (room.items || []).map(i => i.id === itemId ? { ...i, status: nextStatus } : i);
        updateRoom(roomId, { items: newItems });
    };

    const updateItemComment = (roomId, itemId, comment) => {
        const room = rooms.find(r => r.id === roomId);
        const newItems = (room.items || []).map(i => i.id === itemId ? { ...i, comment } : i);
        updateRoom(roomId, { items: newItems });
    };

    // ⭐ NEW: Photo capture scoped to a single checklist item (not the whole room) -
    // required whenever that item is rated yellow or red, since the client/cleaner
    // need to see exactly what's wrong on that specific line, not just the room.
    const updateItemPhotos = (roomId, itemId, photos, previewUrls) => {
        const room = rooms.find(r => r.id === roomId);
        const newItems = (room.items || []).map(i => i.id === itemId ? { ...i, photos, previewUrls } : i);
        updateRoom(roomId, { items: newItems });
    };

    const handleAddItemPhoto = async (e, roomId, itemId) => {
        e.preventDefault();
        e.stopPropagation();

        const room = rooms.find(r => r.id === roomId);
        const currentItem = (room.items || []).find(i => i.id === itemId);

        if (Capacitor.isNativePlatform()) {
            try {
                const image = await NativeCamera.getPhoto({
                    quality: 70,
                    width: 1200,
                    resultType: CameraResultType.Uri,
                    source: CameraSource.Prompt
                });

                const response = await fetch(image.webPath);
                const blob = await response.blob();
                const file = new File([blob], `item_${itemId}_${Date.now()}.jpg`, { type: 'image/jpeg' });

                // ⭐ FIX: This used to just hold `file` in local state and defer the actual
                // upload until the whole form gets submitted (possibly minutes later).
                // Capacitor's native camera returns a temporary `webPath` that can go stale
                // if the app is backgrounded for a while before that later submit happens -
                // and even when it doesn't go stale, deferring means nothing is actually
                // saved server-side until final submit. The web/desktop branch below already
                // uploads immediately and stores the real URL, exactly like a normal job
                // photo - this now does the same on native instead of being the odd one out.
                if (uploadUrl) {
                    toast.info(`Uploading photo...`);
                    try {
                        const uploadFormData = new FormData();
                        uploadFormData.append('photos', file);
                        uploadFormData.append('type', 'area_assessment');
                        uploadFormData.append('roomId', roomId);
                        uploadFormData.append('itemId', itemId);

                        const res = await api.post(uploadUrl, uploadFormData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });

                        const uploadedUrls = res.data.urls || res.data.photos || [];

                        updateItemPhotos(
                            roomId, itemId,
                            [...(currentItem.photos || []), ...uploadedUrls],
                            [...(currentItem.previewUrls || []), ...uploadedUrls]
                        );
                        toast.success("Photo saved!");
                    } catch (uploadErr) {
                        console.error("Native photo upload failed", uploadErr);
                        toast.error("Failed to upload photo immediately.");
                    }
                } else {
                    updateItemPhotos(
                        roomId, itemId,
                        [...(currentItem.photos || []), file],
                        [...(currentItem.previewUrls || []), image.webPath]
                    );
                }
            } catch (err) {
                console.error("Camera error", err);
            }
        } else {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = 'image/*';
            input.onchange = async (event) => {
                const files = Array.from(event.target.files);
                if (!files.length) return;

                if (uploadUrl) {
                    toast.info(`Uploading photo...`);
                    try {
                        const uploadFormData = new FormData();
                        files.forEach(file => uploadFormData.append('photos', file));
                        uploadFormData.append('type', 'area_assessment');
                        uploadFormData.append('roomId', roomId);
                        uploadFormData.append('itemId', itemId);

                        const res = await api.post(uploadUrl, uploadFormData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });

                        const uploadedUrls = res.data.urls || res.data.photos || [];

                        updateItemPhotos(
                            roomId, itemId,
                            [...(currentItem.photos || []), ...uploadedUrls],
                            [...(currentItem.previewUrls || []), ...uploadedUrls]
                        );
                        toast.success("Photo saved!");
                    } catch (error) {
                        console.error("Upload failed", error);
                        toast.error("Failed to upload photo immediately.");
                    }
                } else {
                    updateItemPhotos(roomId, itemId, [...(currentItem.photos || []), ...files], currentItem.previewUrls || []);
                }
            };
            input.click();
        }
    };

    const removeItemPhoto = (e, roomId, itemId, photoIndex) => {
        e.preventDefault();
        e.stopPropagation();
        const room = rooms.find(r => r.id === roomId);
        const currentItem = (room.items || []).find(i => i.id === itemId);

        const newPhotos = [...(currentItem.photos || [])];
        newPhotos.splice(photoIndex, 1);
        const newPreviews = [...(currentItem.previewUrls || [])];
        if (newPreviews.length > 0) newPreviews.splice(photoIndex, 1);

        updateItemPhotos(roomId, itemId, newPhotos, newPreviews);
    };

    const handleAddPhoto = async (e, roomId) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (Capacitor.isNativePlatform()) {
            try {
                const image = await NativeCamera.getPhoto({
                    quality: 70,
                    width: 1200,
                    resultType: CameraResultType.Uri,
                    source: CameraSource.Prompt 
                });

                const response = await fetch(image.webPath);
                const blob = await response.blob();
                const file = new File([blob], `area_${roomId}_${Date.now()}.jpg`, { type: 'image/jpeg' });

                const currentRoom = rooms.find(r => r.id === roomId);

                // ⭐ FIX: same as handleAddItemPhoto above - upload immediately on native
                // instead of holding the File until final form submit, matching the
                // web/desktop branch below.
                if (uploadUrl) {
                    toast.info(`Uploading photo...`);
                    try {
                        const uploadFormData = new FormData();
                        uploadFormData.append('photos', file);
                        uploadFormData.append('type', 'area_assessment');
                        uploadFormData.append('roomId', roomId);

                        const res = await api.post(uploadUrl, uploadFormData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });

                        const uploadedUrls = res.data.urls || res.data.photos || [];

                        updateRoom(roomId, {
                            photos: [...(currentRoom.photos || []), ...uploadedUrls],
                            previewUrls: [...(currentRoom.previewUrls || []), ...uploadedUrls]
                        });
                        toast.success("Photo saved to room!");
                    } catch (uploadErr) {
                        console.error("Native photo upload failed", uploadErr);
                        toast.error("Failed to upload photo immediately.");
                    }
                } else {
                    updateRoom(roomId, {
                        photos: [...(currentRoom.photos || []), file],
                        previewUrls: [...(currentRoom.previewUrls || []), image.webPath]
                    });
                }
            } catch (err) {
                console.error("Camera error", err);
            }
        } else {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = 'image/*';
            input.onchange = async (event) => {
                const files = Array.from(event.target.files);
                
                if (!files.length) return;

                const currentRoom = rooms.find(r => r.id === roomId);
                
                if (uploadUrl) {
                    toast.info(`Uploading photo...`);
                    try {
                        const uploadFormData = new FormData();
                        files.forEach(file => uploadFormData.append('photos', file)); 
                        uploadFormData.append('type', 'area_assessment');
                        uploadFormData.append('roomId', roomId);

                        const res = await api.post(uploadUrl, uploadFormData, {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        });
                        
                        const uploadedUrls = res.data.urls || res.data.photos || []; 
                        
                        updateRoom(roomId, { 
                            photos: [...(currentRoom.photos || []), ...uploadedUrls],
                            previewUrls: [...(currentRoom.previewUrls || []), ...uploadedUrls]
                        });
                        toast.success("Photo saved to room!");
                    } catch (error) {
                        console.error("Upload failed", error);
                        toast.error("Failed to upload photo immediately.");
                    }
                } else {
                    updateRoom(roomId, { photos: [...(currentRoom.photos || []), ...files] });
                }
            };
            input.click();
        }
    };

    const removePhoto = (e, roomId, photoIndex) => {
        e.preventDefault();
        e.stopPropagation();
        const currentRoom = rooms.find(r => r.id === roomId);
        
        const newPhotos = [...currentRoom.photos];
        newPhotos.splice(photoIndex, 1);
        
        const newPreviews = currentRoom.previewUrls ? [...currentRoom.previewUrls] : [];
        if (newPreviews.length > 0) newPreviews.splice(photoIndex, 1);

        const newAnnotations = { ...currentRoom.annotations };
        const pinsToRemove = newAnnotations[photoIndex] || [];
        delete newAnnotations[photoIndex];

        const newNotes = { ...currentRoom.notes };
        pinsToRemove.forEach(pin => {
            delete newNotes[pin.id];
        });

        updateRoom(roomId, { 
            photos: newPhotos, 
            previewUrls: newPreviews,
            annotations: newAnnotations,
            notes: newNotes
        });
    };

    // --- Annotation & Note Logic ---
    const handleAddPin = (e) => {
        if (!activePhoto) return;
        
        const imgElement = e.currentTarget.querySelector('img');
        if (imgElement) {
            const imgRect = imgElement.getBoundingClientRect();
            if (e.clientX < imgRect.left || e.clientX > imgRect.right || 
                e.clientY < imgRect.top || e.clientY > imgRect.bottom) {
                return; 
            }
            
            const x = ((e.clientX - imgRect.left) / imgRect.width) * 100;
            const y = ((e.clientY - imgRect.top) / imgRect.height) * 100;

            const room = rooms.find(r => r.id === activePhoto.roomId);
            const currentAnnotations = room.annotations || {};
            const photoAnnotations = currentAnnotations[activePhoto.photoIndex] || [];
            
            const newPinId = Date.now();

            updateRoom(activePhoto.roomId, {
                annotations: {
                    ...currentAnnotations,
                    [activePhoto.photoIndex]: [...photoAnnotations, { x, y, id: newPinId }]
                },
                notes: {
                    ...(room.notes || {}),
                    [newPinId]: '' 
                }
            });
        }
    };

    const deletePin = (pinId) => {
        if (!activePhoto) return;
        const room = rooms.find(r => r.id === activePhoto.roomId);
        const currentAnnotations = room.annotations || {};
        const photoAnnotations = currentAnnotations[activePhoto.photoIndex] || [];
        const currentNotes = { ...(room.notes || {}) };

        delete currentNotes[pinId];

        updateRoom(activePhoto.roomId, {
            annotations: {
                ...currentAnnotations,
                [activePhoto.photoIndex]: photoAnnotations.filter(p => p.id !== pinId)
            },
            notes: currentNotes
        });
    };

    const clearPins = () => {
        if (!activePhoto) return;
        const room = rooms.find(r => r.id === activePhoto.roomId);
        const currentAnnotations = room.annotations || {};
        const photoAnnotations = currentAnnotations[activePhoto.photoIndex] || [];
        const currentNotes = { ...(room.notes || {}) };

        photoAnnotations.forEach(pin => {
            delete currentNotes[pin.id];
        });

        updateRoom(activePhoto.roomId, {
            annotations: { ...currentAnnotations, [activePhoto.photoIndex]: [] },
            notes: currentNotes
        });
    };

    const handleNoteChange = (pinId, text) => {
        if (!activePhoto) return;
        const room = rooms.find(r => r.id === activePhoto.roomId);
        const currentNotes = room.notes || {};
        updateRoom(activePhoto.roomId, {
            notes: { ...currentNotes, [pinId]: text }
        });
    };

    return (
        <div className="mb-4">
            <label className="block text-sm font-medium mb-3" style={labelStyle}>
                {field.label} {field.required ? '*' : ''}
            </label>
            {isChecklistMode ? (
                // ⭐ NEW: Checklist mode — each area lists its own checklist items, each
                // rated Red/Yellow/Green individually, with an optional comment, plus
                // room-level photo capture (reusing the same photo/annotation logic as before).
                <div className="space-y-4">
                    {/* ⭐ NEW: Overall progress/score across the areas being audited */}
                    {(() => {
                        const applicable = rooms.filter(r => !r.notRequired);
                        const totals = applicable.reduce((acc, r) => {
                            const s = scoreRoom(r);
                            acc.total += s.total; acc.rated += s.rated; acc.points += s.points; acc.max += s.max;
                            return acc;
                        }, { total: 0, rated: 0, points: 0, max: 0 });
                        const skipped = rooms.length - applicable.length;
                        const pct = totals.max > 0 ? (totals.points / totals.max) * 100 : null;
                        return (
                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-50 dark:bg-gray-900/60 border border-slate-200 dark:border-gray-700 px-4 py-3">
                                <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    {totals.rated} of {totals.total} items rated
                                    <span className="text-gray-400 font-normal"> · {applicable.length} area{applicable.length === 1 ? '' : 's'}{skipped > 0 ? `, ${skipped} not required` : ''}</span>
                                </div>
                                {pct !== null && (
                                    <div className="text-sm font-black text-indigo-600 dark:text-indigo-400">
                                        Score so far: {pct.toFixed(1)}%
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {rooms.map((room) => {
                        const roomScore = scoreRoom(room);
                        const allowCopy = canDuplicateRoom(room);
                        const isEditingName = editingNameId === room.id;
                        return (
                        <div key={room.id} className={`rounded-2xl border-2 p-4 sm:p-5 shadow-sm transition-colors ${room.notRequired ? 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
                            {/* ⭐ NEW: Area header - name (editable on areas that can be duplicated), score, and on-site controls */}
                            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                                <div className="min-w-0 flex-1">
                                    {isEditingName && !isPreview ? (
                                        <input
                                            type="text"
                                            autoFocus
                                            value={room.name || ''}
                                            onChange={(e) => renameRoom(room.id, e.target.value)}
                                            onBlur={() => {
                                                if (!(room.name || '').trim()) renameRoom(room.id, getTemplateArea(room)?.name || 'Area');
                                                setEditingNameId(null);
                                            }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                            placeholder="e.g. Office 2 or Finance Office"
                                            className="w-full font-bold text-lg px-2 py-1 rounded-lg border border-indigo-300 dark:border-indigo-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
                                        />
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className={`font-bold text-lg truncate ${room.notRequired ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-800 dark:text-gray-100'}`}>{room.name}</span>
                                            {allowCopy && !isPreview && (
                                                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingNameId(room.id); }} className="p-1 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Rename area">
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-1 text-xs font-semibold">
                                        {room.notRequired ? (
                                            <span className="text-gray-500 dark:text-gray-400">Not required · not scored</span>
                                        ) : (
                                            <span className="text-gray-500 dark:text-gray-400">
                                                {roomScore.rated}/{roomScore.total} rated
                                                {roomScore.pct !== null && (
                                                    <span className={`ml-2 px-1.5 py-0.5 rounded ${roomScore.pct >= (scoringCfg.passThreshold ?? 95) ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
                                                        {roomScore.pct.toFixed(1)}%
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {!isPreview && (
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <button
                                            type="button"
                                            onClick={(e) => toggleNotRequired(e, room.id)}
                                            className={`flex items-center text-xs font-bold px-2.5 py-1.5 rounded-lg border transition-colors ${room.notRequired ? 'bg-gray-700 text-white border-gray-700 dark:bg-gray-200 dark:text-gray-900 dark:border-gray-200' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'}`}
                                            title={room.notRequired ? 'Include this area in the audit again' : 'This area is not at this site / not being audited'}
                                        >
                                            <Ban className="w-3.5 h-3.5 mr-1" /> {room.notRequired ? 'Include again' : 'Not required'}
                                        </button>
                                        {allowCopy && (
                                            <button
                                                type="button"
                                                onClick={(e) => duplicateRoom(e, room.id)}
                                                className="flex items-center text-xs font-bold px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800 transition-colors"
                                                title="Add another one of this area, e.g. a second office"
                                            >
                                                <Copy className="w-3.5 h-3.5 mr-1" /> Add another
                                            </button>
                                        )}
                                        {room.isDuplicate && (
                                            <button
                                                type="button"
                                                onClick={(e) => removeRoom(e, room.id)}
                                                className={`flex items-center text-xs font-bold px-2.5 py-1.5 rounded-lg border transition-colors ${confirmRemoveId === room.id ? 'bg-red-600 text-white border-red-600' : 'border-red-200 text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/50'}`}
                                                title="Remove this copy"
                                            >
                                                <Trash2 className="w-3.5 h-3.5 mr-1" /> {confirmRemoveId === room.id ? 'Tap again to remove' : 'Remove'}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {!room.notRequired && (
                            <>
                            <div className="space-y-2">
                                {(room.items || []).map((item) => {
                                    const isGreen = item.status === 'green';
                                    const isYellow = item.status === 'yellow';
                                    const isRed = item.status === 'red';
                                    const isNa = item.status === 'na';
                                    return (
                                        <div key={item.id}
                                            className={`rounded-xl border p-3 transition-colors duration-200
                                                ${isGreen ? 'bg-green-50 border-green-400 dark:bg-green-900/20 dark:border-green-700' : ''}
                                                ${isYellow ? 'bg-yellow-50 border-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-700' : ''}
                                                ${isRed ? 'bg-red-50 border-red-400 dark:bg-red-900/20 dark:border-red-700' : ''}
                                                ${isNa ? 'bg-slate-100 border-slate-300 dark:bg-slate-800 dark:border-slate-600' : ''}
                                                ${!item.status ? 'bg-gray-50 border-gray-200 dark:bg-gray-900/40 dark:border-gray-700' : ''}
                                            `}
                                        >
                                            <div
                                                onClick={(e) => cycleItemStatus(e, room.id, item.id, item.status)}
                                                className="flex items-center justify-between cursor-pointer"
                                            >
                                                <span className={`text-sm font-medium ${isNa ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'}`}>{item.text}</span>
                                                <span className={`flex-shrink-0 min-w-[1.5rem] h-6 px-1 rounded-full border-2 flex items-center justify-center text-[10px] font-black uppercase
                                                    ${isGreen ? 'bg-green-500 border-green-500 text-white' : ''}
                                                    ${isYellow ? 'bg-yellow-500 border-yellow-500 text-white' : ''}
                                                    ${isRed ? 'bg-red-500 border-red-500 text-white' : ''}
                                                    ${isNa ? 'bg-slate-400 border-slate-400 text-white dark:bg-slate-500 dark:border-slate-500' : ''}
                                                    ${!item.status ? 'bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-600' : ''}
                                                `}>
                                                    {isGreen ? '✓' : isYellow ? '!' : isRed ? '✕' : isNa ? 'N/A' : ''}
                                                </span>
                                            </div>

                                            {/* Comment + photo: required once an item is rated not-acceptable (yellow/red) */}
                                            {(isYellow || isRed) && (
                                                <div className="mt-2 space-y-2" onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="text"
                                                        value={item.comment || ''}
                                                        onChange={(e) => updateItemComment(room.id, item.id, e.target.value)}
                                                        placeholder="Comment required..."
                                                        className={`w-full text-xs px-2 py-1.5 rounded-md border bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 ${!item.comment?.trim() ? 'border-red-400' : 'border-gray-200 dark:border-gray-600'}`}
                                                    />

                                                    <div className="flex items-center justify-between">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => handleAddItemPhoto(e, room.id, item.id)}
                                                            className={`flex items-center text-xs font-bold px-2 py-1.5 rounded-lg ${(item.photos?.length > 0) ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/40' : 'text-red-600 bg-red-50 dark:bg-red-900/30'}`}
                                                        >
                                                            <Camera className="w-3.5 h-3.5 mr-1.5" /> {(item.photos?.length > 0) ? 'Add another' : 'Photo required'}
                                                        </button>
                                                        {(item.photos?.length > 0) && (
                                                            <span className="flex items-center text-xs font-semibold text-gray-500">
                                                                <ImageIcon className="w-3.5 h-3.5 mr-1" /> {item.photos.length}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {item.photos?.length > 0 && (
                                                        <div className="flex gap-2 overflow-x-auto pb-1">
                                                            {item.photos.map((photo, pIdx) => {
                                                                const previewSrc = photo instanceof File ? URL.createObjectURL(photo) : (item.previewUrls?.[pIdx] || photo);
                                                                return (
                                                                    <div key={pIdx} className="relative w-12 h-12 flex-shrink-0 rounded-md overflow-hidden border border-gray-300 dark:border-gray-600 group shadow-sm">
                                                                        <img
                                                                            src={previewSrc}
                                                                            alt="Preview"
                                                                            className="w-full h-full object-cover cursor-pointer group-hover:scale-110 transition-transform duration-300"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                setActivePhoto({ roomId: room.id, itemId: item.id, photoIndex: pIdx, url: previewSrc });
                                                                            }}
                                                                        />
                                                                        <button type="button" onClick={(e) => removeItemPhoto(e, room.id, item.id, pIdx)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-md p-0.5 opacity-90 hover:opacity-100 transition-opacity">
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Room-level photo capture, same behaviour as before but always available (not tied to a single bad status) */}
                            <div className="flex flex-col gap-3 bg-gray-50 dark:bg-gray-900 rounded-xl p-3 border border-gray-100 dark:border-gray-700 mt-3">
                                <div className="flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={(e) => handleAddPhoto(e, room.id)}
                                        className="flex items-center text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/40 px-3 py-2 rounded-lg"
                                    >
                                        <Camera className="w-4 h-4 mr-2" /> Add Photo
                                    </button>
                                    {(room.photos?.length > 0) && (
                                        <span className="flex items-center text-sm font-semibold text-gray-500">
                                            <ImageIcon className="w-4 h-4 mr-1" /> {room.photos.length}
                                        </span>
                                    )}
                                </div>

                                {room.photos?.length > 0 && (
                                    <div className="flex gap-2 overflow-x-auto pb-1 mt-1">
                                        {room.photos.map((photo, pIdx) => {
                                            const previewSrc = photo instanceof File ? URL.createObjectURL(photo) : (room.previewUrls?.[pIdx] || photo);
                                            const hasAnnotations = (room.annotations?.[pIdx]?.length || 0) > 0;
                                            const hasNotes = Object.keys(room.notes || {}).some(key => room.annotations?.[pIdx]?.some(pin => pin.id.toString() === key) && !!room.notes[key]);

                                            return (
                                                <div key={pIdx} className="relative w-14 h-14 flex-shrink-0 rounded-md overflow-hidden border border-gray-300 dark:border-gray-600 group shadow-sm">
                                                    <img
                                                        src={previewSrc}
                                                        alt="Preview"
                                                        className="w-full h-full object-cover cursor-pointer group-hover:scale-110 transition-transform duration-300"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setActivePhoto({ roomId: room.id, photoIndex: pIdx, url: previewSrc });
                                                        }}
                                                    />
                                                    {(hasAnnotations || hasNotes) && (
                                                        <div className="absolute bottom-1 left-1 flex gap-1 pointer-events-none">
                                                            {hasAnnotations && <div className="bg-red-500 w-2 h-2 rounded-full border border-white"></div>}
                                                            {hasNotes && <div className="bg-yellow-500 w-2 h-2 rounded-full border border-white"></div>}
                                                        </div>
                                                    )}
                                                    <button type="button" onClick={(e) => removePhoto(e, room.id, pIdx)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-md p-0.5 opacity-90 hover:opacity-100 transition-opacity">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            </>
                            )}
                        </div>
                        );
                    })}
                </div>
            ) : (
                // ⭐ LEGACY: Original per-room good/bad grid, kept exactly as before.
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {rooms.map((room) => {
                        const isGood = room.status === 'good';
                        const isBad = room.status === 'bad';

                        return (
                            <div key={room.id}
                                onClick={(e) => cycleStatus(e, room.id, room.status)}
                                className={`relative overflow-hidden rounded-2xl border-2 p-5 cursor-pointer transition-all duration-300 shadow-sm
                                    ${isGood ? 'bg-green-50 border-green-500' : ''}
                                    ${isBad ? 'bg-red-50 border-red-500 shadow-red-100 shadow-md' : ''}
                                    ${!room.status ? 'bg-white border-gray-200 hover:border-blue-300 dark:bg-gray-800 dark:border-gray-700' : ''}
                                `}
                            >
                                <div className="flex justify-between items-center">
                                    <span className={`font-bold text-lg 
                                        ${isGood ? 'text-green-700' : ''}
                                        ${isBad ? 'text-red-700' : ''}
                                        ${!room.status ? 'text-gray-700 dark:text-gray-200' : ''}
                                    `}>
                                        {room.name}
                                    </span>
                                    {isGood && <CheckCircle2 className="text-green-500 w-6 h-6" />}
                                    {isBad && <AlertTriangle className="text-red-500 w-6 h-6" />}
                                </div>

                                <div className={`transition-all duration-300 overflow-hidden ${isBad ? 'max-h-60 opacity-100 mt-4' : 'max-h-0 opacity-0 m-0'}`}>
                                    <div className="flex flex-col gap-3 bg-white dark:bg-gray-900 rounded-xl p-3 border border-red-100 dark:border-red-900/30" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-between">
                                            <button 
                                                type="button"
                                                onClick={(e) => handleAddPhoto(e, room.id)}
                                                className="flex items-center text-sm font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/40 px-3 py-2 rounded-lg"
                                            >
                                                <Camera className="w-4 h-4 mr-2" /> Add Photo
                                            </button>
                                            {(room.photos?.length > 0) && (
                                                <span className="flex items-center text-sm font-semibold text-gray-500">
                                                    <ImageIcon className="w-4 h-4 mr-1" /> {room.photos.length}
                                                </span>
                                            )}
                                        </div>

                                        {room.photos?.length > 0 && (
                                            <div className="flex gap-2 overflow-x-auto pb-1 mt-1">
                                                {room.photos.map((photo, pIdx) => {
                                                    const previewSrc = photo instanceof File ? URL.createObjectURL(photo) : (room.previewUrls?.[pIdx] || photo);
                                                    const hasAnnotations = (room.annotations?.[pIdx]?.length || 0) > 0;
                                                    const hasNotes = Object.keys(room.notes || {}).some(key => room.annotations?.[pIdx]?.some(pin => pin.id.toString() === key) && !!room.notes[key]);

                                                    return (
                                                        <div key={pIdx} className="relative w-14 h-14 flex-shrink-0 rounded-md overflow-hidden border border-gray-300 dark:border-gray-600 group shadow-sm">
                                                            <img 
                                                                src={previewSrc} 
                                                                alt="Preview" 
                                                                className="w-full h-full object-cover cursor-pointer group-hover:scale-110 transition-transform duration-300" 
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setActivePhoto({ roomId: room.id, photoIndex: pIdx, url: previewSrc });
                                                                }}
                                                            />
                                                            {(hasAnnotations || hasNotes) && (
                                                                <div className="absolute bottom-1 left-1 flex gap-1 pointer-events-none">
                                                                    {hasAnnotations && <div className="bg-red-500 w-2 h-2 rounded-full border border-white"></div>}
                                                                    {hasNotes && <div className="bg-yellow-500 w-2 h-2 rounded-full border border-white"></div>}
                                                                </div>
                                                            )}
                                                            <button type="button" onClick={(e) => removePhoto(e, room.id, pIdx)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-md p-0.5 opacity-90 hover:opacity-100 transition-opacity">
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}

            {/* 🔥 Annotation & Notes Lightbox (TELEPORTED TO BODY WITH EXTREME Z-INDEX & SCROLL FIX) */}
            {activePhoto && typeof document !== 'undefined' && createPortal((
                <div 
                    className="bg-gray-900/95 p-4 sm:p-6 overflow-y-auto flex flex-col items-center" 
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999999 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setActivePhoto(null);
                    }}
                >
                    {(() => {
                        const activeRoom = rooms.find(r => r.id === activePhoto.roomId);
                        const pins = activeRoom?.annotations?.[activePhoto.photoIndex] || [];

                        return (
                            <div 
                                className="relative w-full max-w-5xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col md:flex-row overflow-hidden animate-in zoom-in-95 duration-300 md:max-h-[90vh] my-auto shrink-0" 
                                onClick={e => e.stopPropagation()}
                            >
                                {/* Left: Interactive Canvas */}
                                <div 
                                    className="relative flex-1 bg-gray-100 dark:bg-gray-800 flex items-center justify-center group cursor-crosshair min-h-[40vh] md:min-h-0 md:overflow-auto p-4" 
                                    onClick={handleAddPin}
                                >
                                    <div className="relative m-auto">
                                        <img 
                                            src={activePhoto.url} 
                                            alt="Annotate" 
                                            className="max-w-full object-contain shadow-lg rounded-md pointer-events-none" 
                                        />
                                        
                                        {/* Empty State Instruction */}
                                        {pins.length === 0 && (
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                <div className="bg-gray-900/70 text-white px-5 py-3 rounded-full flex items-center backdrop-blur-md font-semibold shadow-xl">
                                                    <MousePointerClick className="w-5 h-5 mr-2 text-blue-300" /> Tap image to drop warning pin
                                                </div>
                                            </div>
                                        )}

                                        {/* Render Pins */}
                                        {pins.map((pin, index) => (
                                            <div 
                                                key={pin.id} 
                                                className="absolute w-8 h-8 bg-red-500 rounded-full border-2 border-white shadow-xl transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center animate-in zoom-in pointer-events-none"
                                                style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                                            >
                                                <span className="text-white text-xs font-bold">{index + 1}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right: Notes Panel */}
                                <div className="w-full md:w-96 p-4 sm:p-6 flex flex-col bg-white dark:bg-gray-900 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-800 shrink-0">
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <h3 className="font-bold text-xl text-gray-800 dark:text-white flex items-center">
                                                <Camera className="w-5 h-5 mr-2 text-blue-500" />
                                                Marks & Notes
                                            </h3>
                                            <p className="text-sm font-medium text-gray-500 mt-1">
                                                {activeRoom.name} <span className="mx-2">•</span> Photo {activePhoto.photoIndex + 1}
                                            </p>
                                        </div>
                                        <button 
                                            onClick={() => setActivePhoto(null)} 
                                            className="p-2 bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-gray-800 dark:hover:text-white rounded-full transition-colors"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                    
                                    <div className="flex-1 flex flex-col space-y-4 md:overflow-y-auto pr-1 md:pr-2 custom-scrollbar">
                                        {pins.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-10">
                                                <MousePointerClick className="w-8 h-8 mb-2 opacity-50" />
                                                <p className="text-sm font-medium text-center">Tap on the image to drop a hazard pin.</p>
                                            </div>
                                        ) : (
                                            pins.map((pin, index) => (
                                                <div key={pin.id} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700 relative">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center">
                                                            <span className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs mr-2 shadow-sm">{index + 1}</span>
                                                            Hazard {index + 1}
                                                        </label>
                                                        <button 
                                                            onClick={() => deletePin(pin.id)} 
                                                            className="text-red-500 hover:text-red-700 p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <textarea 
                                                        value={activeRoom.notes?.[pin.id] || ''}
                                                        onChange={(e) => handleNoteChange(pin.id, e.target.value)}
                                                        placeholder="Type details about this specific issue..."
                                                        className="w-full min-h-[80px] p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 outline-none resize-none transition-shadow text-sm"
                                                    />
                                                </div>
                                            ))
                                        )}
                                        
                                        {pins.length > 0 && (
                                            <button 
                                                onClick={clearPins} 
                                                className="w-full py-3 mt-4 text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-xl transition-colors flex items-center justify-center border border-red-100 dark:border-red-900/50"
                                            >
                                                <X className="w-4 h-4 mr-2" /> Clear All Pins
                                            </button>
                                        )}
                                    </div>

                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActivePhoto(null);
                                        }} 
                                        className="w-full mt-4 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-xl transition-all shadow-md active:scale-[0.98] flex justify-center items-center shrink-0"
                                    >
                                        Save Evidence
                                    </button>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            ), document.body)}
        </div>
    );
};


const FormRenderer = forwardRef(({ formDefinition, onSubmit, isPreview = false, isLoading: isSubmitting = false, submitButtonText = "Submit Form", originalQuoteRequestId = null, jobId = null, customerId = null, siteId = null, pageId = null, signedAt = null, qrSource = null, assessmentPhotoUploadUrl = null }, ref) => {
    // ⭐ INTEGRATION FIX: Use Global Settings Context for BOTH Settings and Stock
    const { settings, stockItems } = useSettings();
    
    // formData now holds an array of files for 'file' type fields (multiple_files)
    const [formData, setFormData] = useState(() => formDefinition?.initialData || {});
    const [formErrors, setFormErrors] = useState({});
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isProcessingLocal, setIsProcessingLocal] = useState(false); // Local state for submit protection
    
    // Derived from Context
    const companyJobTypes = useMemo(() => settings?.jobTypes || [], [settings]);
    
    // ⭐ FIX: Using global stockItems instead of local state
    const allStockItems = useMemo(() => stockItems || [], [stockItems]);

    const [currentTaskIndex, setCurrentTaskIndex] = useState(0);

    const autocompleteRefs = useRef({});
    const sigPadRefs = useRef({});

    const { formatCurrency } = useCurrency();

    // Alias 'file' to 'multiple_files' for better clarity in the code, but keep 'file' as the type from builder
    const schema = useMemo(() => formDefinition?.formSchema || [], [formDefinition]);
    const globalStyles = useMemo(() => ({ ...defaultGlobalStyles, ...(formDefinition?.settings?.styles || {}) }), [formDefinition]);

    const groupedFieldsForRendering = useMemo(() => {
        const grouped = {};
        const standaloneFields = [];
        const taskOrder = [];

        schema.forEach(row => {
            (row.columns || []).forEach(column => {
                (column.fields || []).forEach(field => {
                    if (field.mapping && field.mapping.startsWith('task_item.')) {
                        const taskIdMatch = field.mapping.match(/task_item\.([^.]+)\./);
                        if (taskIdMatch && taskIdMatch[1]) {
                            const taskId = taskIdMatch[1];
                            if (!grouped[taskId]) {
                                grouped[taskId] = {
                                    id: taskId,
                                    descriptionField: null,
                                    completedField: null,
                                    reasonField: null,
                                    allTaskFields: [],
                                    rowId: row.id,
                                    columnId: column.id
                                };
                                taskOrder.push(taskId); // Preserve original order
                            }
                            if (field.mapping.endsWith('.description')) {
                                grouped[taskId].descriptionField = field;
                            } else if (field.mapping.endsWith('.completed')) {
                                grouped[taskId].completedField = field;
                            } else if (field.mapping.endsWith('.reason')) {
                                grouped[taskId].reasonField = field;
                            }
                            grouped[taskId].allTaskFields.push(field);
                        }
                    } else {
                        standaloneFields.push({ field, rowId: row.id, columnId: column.id });
                    }
                });
            });
        });

        const orderedTasks = taskOrder.map(taskId => grouped[taskId]);

        const standaloneRows = [];
        const rowMap = new Map();

        standaloneFields.forEach(({ field, rowId, columnId }) => {
            if (!rowMap.has(rowId)) {
                const originalRow = schema.find(r => r.id === rowId);
                if (originalRow) {
                    rowMap.set(rowId, { ...originalRow, columns: new Map() });
                    standaloneRows.push(rowMap.get(rowId));
                }
            }
            const row = rowMap.get(rowId);
            if (row) {
                if (!row.columns.has(columnId)) {
                    const originalCol = schema.find(r => r.id === rowId)?.columns.find(c => c.id === columnId);
                    if (originalCol) {
                        row.columns.set(columnId, { ...originalCol, fields: [] });
                    }
                }
                const col = row.columns.get(columnId);
                if (col) {
                    col.fields.push(field);
                }
            }
        });

        standaloneRows.forEach(row => {
            row.columns = Array.from(row.columns.values());
        });

        return { groupedTasks: orderedTasks, standaloneRows };
    }, [schema]);

    const { topContentRows, bottomContentRows } = useMemo(() => {
        const top = [];
        const bottom = [];
        
        groupedFieldsForRendering.standaloneRows.forEach(row => {
            const isManualRow = row.columns.some(col => 
                col.fields.some(field => field.type === 'static_pdf')
            );

            if (isManualRow) {
                top.push(row);
            } else {
                bottom.push(row);
            }
        });

        return { topContentRows: top, bottomContentRows: bottom };
    }, [groupedFieldsForRendering]);

    const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const associatedLeadId = queryParams.get('leadId');
    
    const { isMapsLoaded: isLoaded, isMapsLoadError: loadError } = useContext(MapsApiContext);

    useEffect(() => {
        if (loadError) {
            console.error("FormRenderer: Google Maps API load error:", loadError);
        }
    }, [loadError]);


    const findFieldInSchema = useCallback((fieldName) => {
        for (const row of schema) {
            for (const col of (row.columns || [])) {
                const found = (col.fields || []).find(f => f.name === fieldName);
                if (found) return found;
            }
        }
        return null;
    }, [schema]);

    const initializeFormData = useCallback(() => {
        const initialData = {};
        if (Array.isArray(schema)) {
            schema.forEach(row => {
                (row.columns || []).forEach(column => {
                    (column.fields || []).forEach(field => {
                        if (field.type === 'static_text' || field.type === 'static_pdf') { 
                            initialData[field.name] = null;
                        } else if (field.mapping && field.mapping.startsWith('task_item')) {
                            if (field.mapping.endsWith('.completed')) {
                                initialData[field.name] = null;
                            } else {
                                initialData[field.name] = '';
                            }
                        } else if (field.type === 'address') {
                            initialData[field.name] = { street: '', city: '', county: '', postcode: '', country: '', fullAddress: '', latitude: null, longitude: null };
                        } else if (field.type === 'checkbox') {
                            initialData[field.name] = false;
                        } else if (field.type === 'file') {
                            initialData[field.name] = [];
                        } else if (field.type === 'e-sign') {
                            initialData[field.name] = null;
                        } else if (field.type === 'stock_items') {
                            initialData[field.name] = [];
                        } else if (field.type === 'star_rating') { 
                            initialData[field.name] = 0;
                        } else if (field.type === 'area_assessment') {
                            // ⭐ FIX: Prefer the new checklist-mode `areas` when present, so a
                            // field that's been upgraded to checklist scoring doesn't silently
                            // seed itself with the old flat-room shape just because leftover
                            // `options` are still sitting on the field from before the migration.
                            if (Array.isArray(field.areas) && field.areas.length > 0) {
                                initialData[field.name] = field.areas.map(area => ({
                                    id: area.id,
                                    name: area.name,
                                    photos: [],
                                    annotations: {},
                                    notes: {},
                                    items: (area.items || []).map(item => ({
                                        id: item.id,
                                        text: item.text,
                                        status: null,
                                        comment: '',
                                        photos: [],
                                    })),
                                }));
                            } else {
                                initialData[field.name] = (field.options || []).map(opt => ({
                                    id: String(opt).toLowerCase().replace(/[^a-z0-9]/g, '_'),
                                    name: String(opt),
                                    status: null,
                                    photos: []
                                }));
                            }
                        } else {
                            initialData[field.name] = '';
                        }
                    });
                });
            });
        }
        return initialData;
    }, [schema]);

    useEffect(() => {
        setFormData(formDefinition?.initialData && Object.keys(formDefinition.initialData).length > 0
            ? formDefinition.initialData
            : initializeFormData()
        );
        setFormErrors({});
        setIsFormSubmitted(false);
        setCurrentTaskIndex(0);
    }, [formDefinition?.formSchema, formDefinition?.initialData, initializeFormData]);

    useEffect(() => {
        const dataToLoad = formDefinition?.initialData || {};

        schema.forEach(row => {
            (row.columns || []).forEach(column => {
                (column.fields || []).forEach(field => {
                    if (field.type === 'e-sign') {
                        const sigPad = sigPadRefs.current[field.name];
                        const savedData = dataToLoad[field.name];
                        
                        if (sigPad && savedData && typeof savedData === 'string' && savedData.startsWith('data:image/')) {
                            sigPad.clear();
                            sigPad.fromDataURL(savedData);
                        } else if (sigPad) {
                            sigPad.clear();
                        }
                    }
                });
            });
        });
    }, [formDefinition?.initialData, schema]);


    useImperativeHandle(ref, () => ({
        resetForm() {
            setFormData(initializeFormData());
            setFormErrors({});
            setIsFormSubmitted(false);
            setCurrentTaskIndex(0);
            Object.values(sigPadRefs.current).forEach(sigPad => {
                if(sigPad) sigPad.clear();
            });
        }
    }));

    const isFieldVisible = useCallback((field) => {
        if (isPreview) {
            return true;
        }

        if (!field.conditional) {
            return true;
        }

        const watchedFieldName = field.conditional.field;
        const requiredValue = field.conditional.value;
        const currentWatchedValue = formData[watchedFieldName];

        return currentWatchedValue !== undefined && String(currentWatchedValue) === String(requiredValue);
    }, [formData, isPreview]);

    const validateField = useCallback((field, value, currentFormData, errorsAccumulator) => {
        const isValueEffectivelyEmpty = (val, type) => {
            // ⭐ FIX: e-sign must be checked BEFORE the generic null/undefined
            // check below. formData[field.name] for an e-sign field is always
            // null (the signature only ever lives in sigPadRefs, never in
            // formData), so the old null-check short-circuit made this branch
            // dead code and every e-sign field was reported as required even
            // when it had a real signature drawn on it.
            if (type === 'e-sign') {
                const sigPad = sigPadRefs.current[field.name];
                return !sigPad || sigPad.isEmpty();
            }
            if (val === null || val === undefined) return true;
            if (typeof val === 'string') return val.trim() === '';
            if (type === 'number' && (isNaN(parseFloat(val)) || parseFloat(val) === 0)) return true;
            if (type === 'star_rating' && (isNaN(parseInt(val)) || parseInt(val) === 0)) return true;
            if (type === 'file' && Array.isArray(val)) return val.length === 0;
            if (Array.isArray(val) && type !== 'area_assessment') return val.length === 0;
            if (type === 'address' && typeof val === 'object') {
                return !val.street || val.street.trim() === ''; 
            }
            if (type === 'area_assessment' && Array.isArray(val)) {
                // ⭐ FIX: Checklist-mode areas never had a room-level `status` (it's
                // undefined, not null), so this check could never fail for them. Checklist
                // completeness is now enforced below for every checklist audit; this keeps
                // the original room-level check for legacy good/bad fields only.
                const isChecklist = val.some(room => Array.isArray(room.items) && room.items.length > 0);
                if (isChecklist) return false;
                return val.some(room => room.status === null);
            }
            return false;
        };

        if (!isFieldVisible(field)) {
            return true;
        }

        if (field.mapping && field.mapping.endsWith('.description') && field.type === 'static_text') {
            return true;
        }

        if (field.type === 'static_text' || field.type === 'static_pdf') {
            return true;
        }

        if (field.required && isValueEffectivelyEmpty(value, field.type)) {
            errorsAccumulator[field.name] = `${field.label || 'This field'} is required.`;
            return false;
        }

        if (field.type === 'email' && value && !/.+@.+\..+/.test(value)) {
            errorsAccumulator[field.name] = 'Invalid email format.';
            return false;
        }

        // ⭐ NEW: Any checklist item rated yellow or red must have both a comment
        // and at least one photo before the form can be submitted - this is the
        // evidence trail between office, client, and cleaner, so it's enforced
        // regardless of whether the field itself is marked "required".
        if (field.type === 'area_assessment' && Array.isArray(value)) {
            // ⭐ NEW: Every item in an area that's being audited must be rated. The only way
            // to leave items unrated is to mark the whole area "Not required" - so every
            // score is based on items that were actually checked.
            const isChecklist = value.some(room => Array.isArray(room.items) && room.items.length > 0);
            if (isChecklist) {
                const unratedByArea = [];
                value.forEach(room => {
                    if (room.notRequired) return;
                    const unrated = (room.items || []).filter(item => !item.status).length;
                    if (unrated > 0) unratedByArea.push(`${room.name} (${unrated})`);
                });
                if (unratedByArea.length > 0) {
                    const shown = unratedByArea.slice(0, 3).join(', ');
                    const extra = unratedByArea.length > 3 ? ` (+${unratedByArea.length - 3} more areas)` : '';
                    errorsAccumulator[field.name] = `Rate every item, or mark the area Not required: ${shown}${extra}`;
                    return false;
                }
            }

            const incomplete = [];
            value.forEach(room => {
                if (room.notRequired) return; // ⭐ NEW: skipped areas need no evidence
                (room.items || []).forEach(item => {
                    if (item.status === 'yellow' || item.status === 'red') {
                        const hasComment = item.comment && item.comment.trim() !== '';
                        const hasPhoto = Array.isArray(item.photos) && item.photos.length > 0;
                        if (!hasComment || !hasPhoto) {
                            incomplete.push(`${room.name}: ${item.text}`);
                        }
                    }
                });
            });
            if (incomplete.length > 0) {
                const shown = incomplete.slice(0, 3).join(', ');
                const extra = incomplete.length > 3 ? ` (+${incomplete.length - 3} more)` : '';
                errorsAccumulator[field.name] = `Add a photo and comment for: ${shown}${extra}`;
                return false;
            }
        }

        return true;
    }, [isFieldVisible]);


    const validateTaskReason = useCallback((taskGroup, currentFormData, errorsAccumulator) => {
        if (!taskGroup || !taskGroup.reasonField || !taskGroup.completedField) return true;

        const completedValue = currentFormData[taskGroup.completedField.name];
        const successOption = taskGroup.completedField.options?.[0] || 'Yes';

        if (completedValue !== successOption && completedValue !== true && completedValue !== undefined && completedValue !== null && completedValue !== '') {
            const reasonField = taskGroup.reasonField;
            const reasonValue = currentFormData[reasonField.name];
            
            if (reasonField.required && (!reasonValue || reasonValue.trim() === '')) {
                errorsAccumulator[reasonField.name] = reasonField.placeholder || 'A reason is required to move on.';
                return false;
            }
        }
        return true;
    }, []);

    const goToNextTask = () => {
        setTimeout(() => {
            setCurrentTaskIndex(prev => prev + 1);
        }, 300);
    };

    const handleNextTaskClick = () => {
        const currentTask = groupedFieldsForRendering.groupedTasks[currentTaskIndex];
        const newErrors = {};

        const isTaskValid = validateTaskReason(currentTask, formData, newErrors);
        
        if (!isTaskValid) {
            setFormErrors(prev => ({ ...prev, ...newErrors }));
            toast.error("Please provide a reason before moving on.");
            return;
        }

        if (currentTask && currentTask.reasonField) {
            setFormErrors(prev => { 
                const updatedErrors = { ...prev };
                delete updatedErrors[currentTask.reasonField.name];
                return updatedErrors; 
            });
        }
        
        goToNextTask();
    };


    const handleTaskCompletionChange = (fieldName, value) => {
        setFormData(prev => {
            const newFormData = { ...prev, [fieldName]: value };
            
            const currentTask = groupedFieldsForRendering.groupedTasks.find(task => task.completedField.name === fieldName);

            if (currentTask && currentTask.reasonField) {
                 setFormErrors(prevErrors => { 
                    const newErrors = { ...prevErrors }; 
                    delete newErrors[currentTask.reasonField.name]; 
                    return newErrors; 
                 });
            }
            
            const affectedFields = schema.flatMap(row => row.columns.flatMap(col => col.fields)).filter(f =>
                f.conditional && f.conditional.field === fieldName
            );
    
            affectedFields.forEach(affectedField => {
                if (String(value) !== String(affectedField.conditional.value)) {
                    newFormData[affectedField.name] = initializeFormData()[affectedField.name];
                    setFormErrors(prevErrors => { 
                        const updatedErrors = { ...prevErrors };
                        delete updatedErrors[affectedField.name];
                        return updatedErrors;
                    });
                }
            });
    
            return newFormData;
        });
    
        const currentTask = groupedFieldsForRendering.groupedTasks.find(task => task.completedField.name === fieldName);
        const successOption = currentTask?.completedField?.options?.[0] || 'Yes';
        const isNeutral = !currentTask?.reasonField;
        
        if (value === successOption || value === true || isNeutral) {
            goToNextTask();
        }
    };


    const handleChange = useCallback((e) => {
        const { name, value, type, checked, files } = e.target;
        const fieldDef = findFieldInSchema(name);

        if (fieldDef && fieldDef.type === 'file') {
            if (files.length > 0) {
                const newFiles = Array.from(files);
                setFormData(prev => ({ ...prev, [name]: newFiles }));
            } else {
                setFormData(prev => ({ ...prev, [name]: [] }));
            }
            
            setFormErrors(prev => { const newErrors = { ...prev }; delete newErrors[name]; return newErrors; });
            return;
        }

        let newValue = (type === 'checkbox') ? checked : value;
        if (newValue && typeof newValue === 'object' && 'value' in newValue) {
            newValue = newValue.value;
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));

        if (isFormSubmitted && formErrors[name]) {
            if (fieldDef) {
                const tempErrors = {};
                validateField(fieldDef, newValue, { ...formData, [name]: newValue }, tempErrors);
                if (!tempErrors[name]) {
                    setFormErrors(prev => { const newErrors = { ...prev }; delete newErrors[name]; return newErrors; });
                }
            }
        }
    }, [findFieldInSchema, formErrors, isFormSubmitted, formData, validateField]);

    const handleImmediateFileUpload = async (e, fieldName) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        if (jobId) {
            toast.info('Uploading files securely...');
            try {
                const uploadFormData = new FormData();
                files.forEach(file => uploadFormData.append('photos', file));
                
                const res = await api.post(`/jobs/${jobId}/photos`, uploadFormData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                const uploadedUrls = res.data.urls || res.data.photos || [];
                
                setFormData(prev => {
                    const existing = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
                    return { ...prev, [fieldName]: [...existing, ...uploadedUrls] };
                });
                
                setFormErrors(prev => { 
                    const newErrors = { ...prev }; 
                    delete newErrors[fieldName]; 
                    return newErrors; 
                });
                toast.success('Files attached successfully!');
            } catch (error) {
                console.error("Upload failed", error);
                toast.error('Failed to upload files immediately.');
            }
        } else {
            setFormData(prev => {
                const existing = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
                return { ...prev, [fieldName]: [...existing, ...files] };
            });
        }
    };

    const validateForm = useCallback(() => {
        const newErrors = {};
        let allFieldsValid = true;

        schema.forEach(row => {
            (row.columns || []).forEach(column => {
                (column.fields || []).forEach(field => {
                    if (isFieldVisible(field)) {
                        const value = formData[field.name];
                        const isValid = validateField(field, value, formData, newErrors);
                        if (!isValid) {
                            allFieldsValid = false;
                        }
                    } else {
                        delete newErrors[field.name];
                    }
                });
            });
        });

        if (!allFieldsValid) {
            toast.error("Please correct the errors in the form.");
        }
        setFormErrors(newErrors);
        return allFieldsValid;
    }, [formData, schema, isFieldVisible, validateField]);

    const handleSubmitInternal = async (e) => {
        e.preventDefault();

        // Prevent multiple submission clicks locally
        if (isSubmitting || isProcessingLocal) return;

        setIsFormSubmitted(true);

        if (isPreview) {
            toast.info('This is a form preview. Submissions are not processed.');
            return;
        }

        if (!validateForm()) {
            return;
        }

        // Lock form during submission prep
        setIsProcessingLocal(true);

        const submissionFormData = new FormData();
        const allFieldsInSchema = [];

        schema.forEach(row => {
            (row.columns || []).forEach(col => {
                (col.fields || []).forEach(field => {
                    allFieldsInSchema.push(field);
                });
            });
        });

        const taskItems = {};
        
        allFieldsInSchema.forEach(field => {
            const fieldName = field.name;
            let fieldValue = formData[fieldName];

            if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
                fieldValue = fieldValue.value;
            }

            if (!isFieldVisible(field) || field.type === 'static_pdf' || (field.type === 'static_text' && !field.mapping?.startsWith('task_item'))) {
                return;
            }

            const keyName = field.mapping
                ? field.mapping.split('.').pop()
                : field.name;

            if (fieldName === 'jobType') {
                submissionFormData.append('jobType', fieldValue);
                return;
            }

            if (field.type === 'e-sign') {
                const sigPad = sigPadRefs.current[fieldName];
                const dataUrl = (sigPad && !sigPad.isEmpty()) ? sigPad.toDataURL() : null;
                if (dataUrl) {
                    submissionFormData.append(keyName, dataUrl);
                } else {
                    submissionFormData.append(keyName, ''); 
                }
            } else if (field.type === 'file') {
                if (Array.isArray(fieldValue)) {
                    const urls = fieldValue.filter(f => typeof f === 'string');
                    const rawFiles = fieldValue.filter(f => f instanceof File);
                    
                    if (urls.length > 0) {
                        submissionFormData.append(keyName, JSON.stringify(urls));
                    }
                    if (rawFiles.length > 0) {
                        rawFiles.forEach((file) => {
                            submissionFormData.append(fieldName, file); 
                        });
                    }
                }
            } else if (field.type === 'stock_items') {
                submissionFormData.append(keyName, JSON.stringify(fieldValue));
            } else if (field.type === 'address') {
                submissionFormData.append('address', JSON.stringify(fieldValue));
            } else if (field.type === 'area_assessment') {
                const roomsData = [];
                (fieldValue || []).forEach((room) => {
                    const rawFiles = (room.photos || []).filter(p => p instanceof File);
                    const uploadedUrls = (room.photos || []).filter(p => typeof p === 'string');

                    // ⭐ NEW: Same File -> uploaded URL split, but per checklist item -
                    // item-level photos are required whenever that item is rated
                    // yellow or red, so they need to survive submission too.
                    const itemsData = (room.items || []).map(item => {
                        const itemRawFiles = (item.photos || []).filter(p => p instanceof File);
                        const itemUploadedUrls = (item.photos || []).filter(p => typeof p === 'string');

                        if (itemRawFiles.length > 0) {
                            itemRawFiles.forEach((file) => {
                                submissionFormData.append(`${keyName}_${room.id}_${item.id}_photos`, file);
                            });
                        }

                        return {
                            id: item.id,
                            text: item.text,
                            status: item.status,
                            comment: item.comment || '',
                            uploadedUrls: itemUploadedUrls,
                        };
                    });

                    const roomMeta = { 
                        id: room.id, 
                        name: room.name, 
                        status: room.status, 
                        // ⭐ NEW: On-site audit layout - kept on the saved answer so results,
                        // scoring and PDFs know which areas were skipped or added on site.
                        notRequired: !!room.notRequired,
                        isDuplicate: !!room.isDuplicate,
                        sourceAreaId: room.sourceAreaId || null, 
                        photoCount: room.photos?.length || 0,
                        uploadedUrls: uploadedUrls,
                        annotations: room.annotations || {},
                        notes: room.notes || {},
                        items: itemsData,
                    };
                    roomsData.push(roomMeta);
                    
                    if (rawFiles.length > 0) {
                        rawFiles.forEach((file) => {
                            submissionFormData.append(`${keyName}_${room.id}_photos`, file);
                        });
                    }
                });
                submissionFormData.append(keyName, JSON.stringify(roomsData));

            } else if (field.mapping && field.mapping.startsWith('task_item')) {
                const parts = field.mapping.split('.');
                const taskId = parts[1];
                const taskProperty = parts[2];

                if (!taskItems[taskId]) {
                    taskItems[taskId] = { taskId: taskId, description: '', completed: false, reason: '' };
                }
                
                if (taskProperty === 'completed') {
                    const successOption = field.options && field.options.length > 0 ? field.options[0] : 'Yes';
                    taskItems[taskId][taskProperty] = (fieldValue === successOption || fieldValue === true || fieldValue === 'Yes');
                    
                    if (!taskItems[taskId].description || taskItems[taskId].description === '') {
                        taskItems[taskId].description = field.label || 'Task';
                    }
                } else if (taskProperty === 'description') {
                    taskItems[taskId][taskProperty] = formData[fieldName] || field.label || field.placeholder || 'Task';
                } else {
                    taskItems[taskId][taskProperty] = fieldValue;
                }
            } else {
                submissionFormData.append(keyName, fieldValue ?? '');
            }
        });

        submissionFormData.append('tasks', JSON.stringify(Object.values(taskItems)));

        if (formDefinition.purpose) {
            submissionFormData.append('purpose', formDefinition.purpose);
        }
        if (associatedLeadId) {
            submissionFormData.append('associatedLeadId', associatedLeadId);
        }
        if (originalQuoteRequestId) {
            submissionFormData.append('originalQuoteRequestId', originalQuoteRequestId);
        }
        if (jobId) {
            submissionFormData.append('jobId', jobId);
        }
        if (customerId) {
            submissionFormData.append('customerId', customerId);
        }
        if (siteId) {
            submissionFormData.append('siteId', siteId);
        }
        if (pageId) {
            submissionFormData.append('pageId', pageId);
        }
        // ⭐ NEW: Passed straight through from the '?source=slug' query param
        // a 'form' QR campaign redirect appends to the public form URL, so
        // publicFormController.submitPublicForm can attribute the lead back
        // to that campaign. Mirrors the siteId/pageId pattern above.
        if (qrSource) {
            submissionFormData.append('qrSource', qrSource);
        }
        
        if (formDefinition._id) {
            submissionFormData.append('formId', formDefinition._id);
        }

        console.log("FormRenderer - Final Payload (FormData) to Submit:");
        for (let [key, value] of submissionFormData.entries()) {
            if (value instanceof File) {
                console.log(`  ${key}: [File: ${value.name}, Type: ${value.type}, Size: ${value.size} bytes]`);
            } else {
                console.log(`  ${key}: ${value}`);
            }
        }

        try {
            if (onSubmit) {
                await onSubmit(submissionFormData);
            }
        } finally {
            // Unlock form if it errors out or after it completes
            setIsProcessingLocal(false);
        }
    };

    const containerStyle = { backgroundColor: globalStyles.backgroundColor };
    const commonInputStyles = {
        borderColor: globalStyles.borderColor,
        borderRadius: globalStyles.borderRadius,
        borderWidth: `${globalStyles.globalBorderWidth || 1}px`,
        borderStyle: globalStyles.globalBorderStyle || 'solid',
        color: globalStyles.inputTextColor,
        backgroundColor: globalStyles.inputBackgroundColor,
    };
    const buttonStyle = {
        backgroundColor: globalStyles.primaryColor,
        borderRadius: globalStyles.borderRadius,
        borderColor: globalStyles.borderColor,
        borderWidth: `${globalStyles.globalBorderWidth || 1}px`,
        borderStyle: globalStyles.globalBorderStyle || 'solid',
    };


    if (!schema || schema.length === 0) {
        return (
            <div className="text-gray-600 dark:text-gray-400 p-6 text-center">
                No form schema provided to render.
            </div>
        );
    }

    const isTaskListForm = formDefinition?.purpose === 'reminder_task_list';
    const hasTasks = isTaskListForm && groupedFieldsForRendering.groupedTasks.length > 0;
    const allTasksCompleted = hasTasks && currentTaskIndex >= groupedFieldsForRendering.groupedTasks.length;
    
    const getSuggestedFilenames = (field) => {
        if (!field.suggestedFilenames) return [];
        return field.suggestedFilenames.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    };

    const renderFormRows = (rows) => {
        return rows.map(row => (
            <div key={row.id} className={`grid gap-x-6 gap-y-4 ${
                row.columns.length === 1 ? 'grid-cols-1' :
                row.columns.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                'grid-cols-1 md:grid-cols-3'
            }`}>
                {row.columns.map(column => (
                    <div key={column.id} className="flex flex-col space-y-4">
                        {column.fields.map(field => {
                            if (!isFieldVisible(field)) return null;

                            const fieldValue = formData[field.name];
                            const fieldError = formErrors[field.name] || null;
                            const currentFieldInputStyles = { ...commonInputStyles, ...(field.styles || {}) };
                            const currentFieldLabelStyle = { color: currentFieldInputStyles.labelColor };
                            const isFieldActuallyRequired = field.required && isFieldVisible(field);
                            
                            const suggestedLabels = getSuggestedFilenames(field);

                            const isJobTypeField = field.name === 'jobType' || field.mapping === 'jobType';
                            // ⭐ NEW: If the form builder picked a specific allow-list of job types for this
                            // field (field.allowedJobTypes), only show those on the live form. An empty/missing
                            // list means "show all" — keeps existing forms working unchanged.
                            const allowedJobTypesSet = (field.allowedJobTypes && field.allowedJobTypes.length > 0)
                                ? new Set(field.allowedJobTypes)
                                : null;
                            let fieldOptions = isJobTypeField
                                ? [{ value: '', label: 'Select Job Type' }, ...companyJobTypes
                                      .map(type => {
                                          // ⭐ FIX: companyJobTypes contains full objects ({ name, color, defaultPrice, ... })
                                          // from CompanySetting.jobTypes, not plain strings. Unwrap to the name so the
                                          // option value/label is the actual job type name instead of "[object Object]".
                                          const val = typeof type === 'string' ? type : (type.name || type.title || type.value || '');
                                          return val;
                                      })
                                      .filter(val => val && (!allowedJobTypesSet || allowedJobTypesSet.has(val)))
                                      .map(val => ({ value: val, label: val }))]
                                : (field.options || []).map(opt => ({ value: opt, label: opt }));

                            // Inject an empty placeholder for standard dropdowns so the user is forced to make a choice.
                            // We check for 'select' to ensure we don't accidentally add a "Select..." option to radio buttons!
                            if (field.type === 'select' && !isJobTypeField) {
                                fieldOptions = [
                                    { value: '', label: field.placeholder || 'Select...' },
                                    ...fieldOptions
                                ];
                            }

                            try {
                                switch (field.type) {
                                    case 'static_text':
                                        return (
                                            <div key={field.id} className="prose dark:prose-invert max-w-none">
                                                <h3 style={{color: currentFieldLabelStyle.color}}>{field.label}</h3>
                                                <p className="text-gray-600 dark:text-gray-400 text-sm">{field.placeholder}</p>
                                            </div>
                                        );
                                    case 'static_pdf':
                                        return (
                                            <div key={field.id} className="mb-6">
                                                <h3 className="text-lg font-semibold mb-2" style={currentFieldLabelStyle}>
                                                    {field.label}
                                                </h3>
                                                
                                                {field.staticFileUrl ? (
                                                    <div className="border rounded-lg overflow-hidden shadow-sm bg-gray-100 dark:bg-gray-800">
                                                        <object 
                                                            data={field.staticFileUrl} 
                                                            type="application/pdf" 
                                                            width="100%" 
                                                            className="w-full aspect-[1/1.414] min-h-[70vh]"
                                                        >
                                                            <div className="flex flex-col items-center justify-center h-48 p-4 text-center">
                                                                <p className="mb-4 text-gray-600 dark:text-gray-300">
                                                                    Your browser cannot display this PDF embedded.
                                                                </p>
                                                                <a 
                                                                    href={field.staticFileUrl} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                                >
                                                                    Click here to Open PDF
                                                                </a>
                                                            </div>
                                                        </object>
                                                    </div>
                                                ) : (
                                                    <div className="p-4 border border-dashed border-gray-300 rounded text-gray-500 text-center">
                                                        No PDF configured for this field.
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    case 'stock_items':
                                        return <StockItemsField key={field.id} field={field} formData={formData} setFormData={setFormData} allStockItems={allStockItems} commonInputStyles={commonInputStyles} currentFieldLabelStyle={currentFieldLabelStyle} isFieldActuallyRequired={isFieldActuallyRequired} />;
                                    case 'area_assessment':
                                        return (
                                            <AreaAssessmentInput
                                                key={field.id}
                                                field={field}
                                                value={fieldValue}
                                                onChange={handleChange}
                                                labelStyle={currentFieldLabelStyle}
                                                isPreview={isPreview}
                                                error={fieldError}
                                                jobId={jobId} 
                                                photoUploadUrl={assessmentPhotoUploadUrl}
                                            />
                                        );
                                    case 'text':
                                    case 'email':
                                    case 'phone':
                                    case 'date':
                                    case 'time':
                                    case 'number':
                                        return <ModernInput key={field.id} label={field.label} name={field.name} type={field.type === 'phone' ? 'tel' : field.type} value={fieldValue != null ? fieldValue : ''} onChange={handleChange} placeholder={field.placeholder} required={isFieldActuallyRequired} style={currentFieldInputStyles} labelStyle={currentFieldLabelStyle} error={fieldError} />;
                                    case 'textarea':
                                        return <ModernInput key={field.id} textarea label={field.label} name={field.name} value={fieldValue != null ? fieldValue : ''} onChange={handleChange} placeholder={field.placeholder} required={isFieldActuallyRequired} style={currentFieldInputStyles} labelStyle={currentFieldLabelStyle} error={fieldError} />;
                                    case 'select':
                                        return <ModernSelect key={field.id} label={field.label} name={field.name} value={fieldValue != null ? fieldValue : ''} onChange={handleChange} options={fieldOptions} required={isFieldActuallyRequired} borderColor={currentFieldInputStyles.borderColor} borderRadius={currentFieldInputStyles.borderRadius} borderWidth={parseFloat(currentFieldInputStyles.borderWidth)} borderStyle={currentFieldInputStyles.borderStyle} inputTextColor={currentFieldInputStyles.inputTextColor} inputBackgroundColor={currentFieldInputStyles.inputBackgroundColor} labelStyle={currentFieldLabelStyle} error={fieldError} />;
                                    case 'radio':
                                        return (
                                            <div key={field.id} className="mb-4" style={currentFieldLabelStyle}>
                                                <label className="block text-sm font-medium mb-1">{field.label}{isFieldActuallyRequired ? ' *' : ''}</label>
                                                <div className="flex flex-wrap gap-x-4">
                                                    {fieldOptions.map(option => (
                                                        <div key={option.value} className="flex items-center">
                                                            <input type="radio" id={`${field.name}-${option.value}`} name={field.name} value={option.value} checked={formData[field.name] === option.value} onChange={handleChange} required={isFieldActuallyRequired} className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 mr-2 bg-gray-100 dark:bg-gray-700" style={{ borderColor: currentFieldInputStyles.borderColor, backgroundColor: currentFieldInputStyles.inputBackgroundColor }} />
                                                            <label htmlFor={`${field.name}-${option.value}`} className="ml-2 block text-sm text-gray-700 dark:text-gray-300" style={{color: currentFieldInputStyles.inputTextColor}}>{option.label}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                                {fieldError && <p className="text-red-500 text-xs mt-1">{fieldError}</p>}
                                            </div>
                                        );
                                    case 'checkbox':
                                        return (
                                            <div key={field.id} className="flex items-center mb-4" style={currentFieldLabelStyle}>
                                                <input type="checkbox" id={field.name} name={field.name} checked={!!fieldValue} onChange={handleChange} required={isFieldActuallyRequired} className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-gray-100 dark:bg-gray-700" style={{ borderColor: currentFieldInputStyles.borderColor, backgroundColor: currentFieldInputStyles.inputBackgroundColor }} />
                                                <label htmlFor={field.name} className="ml-2 block text-sm text-gray-900 dark:text-gray-200" style={{color: currentFieldInputStyles.inputTextColor}}>{field.label}</label>
                                                {fieldError && <p className="text-red-500 text-xs mt-1">{fieldError}</p>}
                                            </div>
                                        );
                                    case 'star_rating':
                                        return (
                                            <div key={field.id} className="mb-4">
                                                <label className="block text-sm font-medium mb-2" style={currentFieldLabelStyle}>{field.label}{isFieldActuallyRequired ? ' *' : ''}</label>
                                                <StarRatingInput
                                                    name={field.name}
                                                    value={fieldValue || 0}
                                                    onChange={handleChange}
                                                    totalStars={field.totalStars || 5}
                                                    required={isFieldActuallyRequired}
                                                    error={fieldError}
                                                />
                                            </div>
                                        );
                                    case 'address':
                                        return (
                                            <div
                                                key={field.id}
                                                className="space-y-3 sm:space-y-4 p-3 sm:p-5 rounded-xl border"
                                                style={{
                                                    backgroundColor: currentFieldInputStyles.inputBackgroundColor || '#f9fafb',
                                                    borderColor: currentFieldInputStyles.borderColor || '#e5e7eb'
                                                }}
                                            >
                                                <label className="block text-sm font-bold mb-2 sm:mb-3" style={currentFieldLabelStyle}>
                                                    {field.label}{isFieldActuallyRequired ? ' *' : ''}
                                                </label>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                                                    <div className="md:col-span-2">
                                                        <ModernInput
                                                            label="Street Address"
                                                            name="street"
                                                            value={formData[field.name]?.street || ''}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: { ...prev[field.name], street: e.target.value } }))}
                                                            placeholder="e.g. 123 High Street"
                                                            required={isFieldActuallyRequired}
                                                            style={currentFieldInputStyles}
                                                            error={fieldError}
                                                        />
                                                    </div>
                                                    <div>
                                                        <ModernInput
                                                            label="City / Town"
                                                            name="city"
                                                            value={formData[field.name]?.city || ''}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: { ...prev[field.name], city: e.target.value } }))}
                                                            placeholder="e.g. London"
                                                            required={isFieldActuallyRequired}
                                                            style={currentFieldInputStyles}
                                                        />
                                                    </div>
                                                    <div>
                                                        <ModernInput
                                                            label="Postcode"
                                                            name="postcode"
                                                            value={formData[field.name]?.postcode || ''}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: { ...prev[field.name], postcode: e.target.value } }))}
                                                            placeholder="e.g. SW1A 1AA"
                                                            required={isFieldActuallyRequired}
                                                            style={currentFieldInputStyles}
                                                        />
                                                    </div>
                                                </div>
                                                {fieldError && <p className="text-red-500 text-xs mt-1">{fieldError}</p>}
                                            </div>
                                        );
                                    case 'file':
                                        return (
                                            <div key={field.id} className="mb-4">
                                                <label htmlFor={field.name} className="block text-sm font-medium mb-1" style={currentFieldLabelStyle}>{field.label}{isFieldActuallyRequired ? ' *' : ''}</label>
                                                
                                                {suggestedLabels.length > 0 && (
                                                    <div className="p-2 mb-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md">
                                                        <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-1">Documents Required:</p>
                                                        <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4">
                                                            {suggestedLabels.map((label, i) => (
                                                                <li key={i}>{label}</li>
                                                            ))}
                                                        </ul>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Please select all files at once.</p>
                                                    </div>
                                                )}
                                                
                                                <input 
                                                    type="file" 
                                                    id={field.name} 
                                                    name={field.name} 
                                                    onChange={(e) => handleImmediateFileUpload(e, field.name)} 
                                                    required={isFieldActuallyRequired && (!fieldValue || fieldValue.length === 0)} 
                                                    multiple
                                                    className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/50 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-800/60" 
                                                    style={currentFieldInputStyles} 
                                                />
                                                {Array.isArray(fieldValue) && fieldValue.length > 0 && (
                                                    <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-800 rounded border">
                                                        <span className="font-bold mb-2 block">Files securely attached:</span>
                                                        <ul className="list-disc list-inside ml-2 space-y-1">
                                                            {fieldValue.map((f, i) => (
                                                                <li key={i}>
                                                                    {typeof f === 'string' ? (
                                                                        <a href={f} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                                            View Uploaded File {i + 1}
                                                                        </a>
                                                                    ) : (
                                                                        f.name
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {fieldError && <p className="text-red-500 text-xs mt-1">{fieldError}</p>}
                                            </div>
                                        );
                                    case 'e-sign':
                                        return (
                                            <div key={field.id} className="mb-4">
                                                <label className="block text-sm font-medium mb-1" style={currentFieldLabelStyle}>{field.label}{isFieldActuallyRequired ? ' *' : ''}</label>
                                                <div className="relative w-full h-48 border rounded-lg" style={{ borderColor: currentFieldInputStyles.borderColor }}>
                                                    <SignatureCanvas
                                                        ref={(ref) => (sigPadRefs.current[field.name] = ref)}
                                                        penColor='black'
                                                        canvasProps={{
                                                            className: 'w-full h-full rounded-lg',
                                                            style: { 
                                                                backgroundColor: currentFieldInputStyles.inputBackgroundColor,
                                                                pointerEvents: isPreview ? 'none' : 'auto' 
                                                            }
                                                        }}
                                                        onEnd={() => {
                                                            setFormErrors(prev => {
                                                                const newErrors = { ...prev };
                                                                delete newErrors[field.name]; 
                                                                return newErrors;
                                                            });
                                                        }}
                                                        disabled={isPreview} 
                                                    />
                                                </div>
                                                {!isPreview && (
                                                    <button type="button" onClick={() => sigPadRefs.current[field.name].clear()} className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-1">
                                                        Clear Signature
                                                    </button>
                                                )}
                                                {/* ⭐ FIX: no date stamp here at all previously - the PDF+signature
                                                    upload flow always shows "Signed on: dd/MM/yyyy" under the
                                                    signature, but this read-only preview (what staff/customers see
                                                    reviewing a completed fillable form) had nothing. Same hardcoded
                                                    UK format as that flow, not the country-aware setting. */}
                                                {isPreview && signedAt && (
                                                    <p className="text-xs mt-1" style={{ color: currentFieldLabelStyle.color || '#6b7280' }}>
                                                        Signed on: {new Date(signedAt).toLocaleDateString('en-GB')}
                                                    </p>
                                                )}
                                                {fieldError && <p className="text-red-500 text-xs mt-1">{fieldError}</p>}
                                            </div>
                                        );
                                    default:
                                        // eslint-disable-next-line no-restricted-globals
                                        return <ModernInput key={field.id} label={field.label} name={field.name} type={field.type} value={fieldValue || ''} onChange={handleChange} placeholder={field.placeholder} required={isFieldActuallyRequired} style={currentFieldInputStyles} labelStyle={currentFieldLabelStyle} error={fieldError} />;
                                }
                            } catch (renderError) {
                                console.error(`FormRenderer: Error rendering field '${field.name}' (type: ${field.type}):`, renderError);
                                return (<div key={`error-${field.id}`} className="text-red-500 text-sm p-2 border border-red-300 rounded">Error rendering field: {field.label || field.name}</div>);
                            }
                        })}
                    </div>
                ))}
            </div>
        ));
    };

    return (
        <div className="p-4 sm:p-8 md:p-10 rounded-3xl shadow-2xl backdrop-blur-xl border border-white/50 dark:border-slate-700/50 animate-in fade-in zoom-in-95 duration-500 bg-white/90 dark:bg-slate-900/90" style={containerStyle}>
        {globalStyles.logoUrl && !globalStyles.hideLogo && (
                <div className="mb-6 text-center">
                    <img src={globalStyles.logoUrl} alt="Company Logo" className="mx-auto h-16 w-auto" />
                </div>
            )}
            <form onSubmit={handleSubmitInternal} className="space-y-6">
                
                {topContentRows.length > 0 && (!hasTasks || currentTaskIndex === 0) && (
                    <div className="transition-opacity duration-300 ease-in-out">
                        {renderFormRows(topContentRows)}
                    </div>
                )}

                {hasTasks && !allTasksCompleted && (
                    <div className="border border-gray-200 dark:border-gray-700 p-4 sm:p-6 rounded-lg bg-gray-50/50 dark:bg-gray-700/50 space-y-6 shadow-sm">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Task List</h3>
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full">
                                {currentTaskIndex + 1} / {groupedFieldsForRendering.groupedTasks.length}
                            </span>
                        </div>
                        
                        {groupedFieldsForRendering.groupedTasks.filter((_, index) => index === currentTaskIndex).map(taskGroup => {
                             const completedValue = taskGroup.completedField ? formData[taskGroup.completedField.name] : null;
                             const successOption = taskGroup.completedField?.options?.[0] || 'Yes';
                             const showReasonField = taskGroup.reasonField && completedValue !== successOption && completedValue !== null && completedValue !== undefined;
                             
                             return (
                            <div key={taskGroup.id} className="p-4 rounded-md bg-white dark:bg-gray-800">
                                {taskGroup.descriptionField && (
                                     <div className="mb-6">
                                        {taskGroup.descriptionField.type === 'static_text' ? (
                                            <div className="prose dark:prose-invert max-w-none">
                                                <p className="text-xl font-semibold text-gray-900 dark:text-white" style={{ color: commonInputStyles.labelColor }}>
                                                    {formData[taskGroup.descriptionField.name] || taskGroup.descriptionField.label || 'Task Description'}
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-xl font-semibold text-gray-900 dark:text-white" style={{ color: commonInputStyles.labelColor }}>
                                                {formData[taskGroup.descriptionField.name] || taskGroup.descriptionField.label || 'Task Description'}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {taskGroup.completedField && (
                                    <div key={taskGroup.completedField.id} className="mt-4 space-y-4">
                                        {taskGroup.completedField.type === 'checkbox' ? (
                                            <div className="flex items-center pl-2">
                                                <input
                                                    type="checkbox"
                                                    id={taskGroup.completedField.name}
                                                    checked={!!completedValue}
                                                    onChange={(e) => handleTaskCompletionChange(taskGroup.completedField.name, e.target.checked)}
                                                    className="w-6 h-6 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                                                />
                                                <label htmlFor={taskGroup.completedField.name} className="ml-3 text-lg font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                                                    {taskGroup.completedField.label || 'Mark as completed'}
                                                </label>
                                            </div>
                                        ) : taskGroup.completedField.type === 'select' ? (
                                            <ModernSelect
                                                value={completedValue || ''}
                                                onChange={(e) => handleTaskCompletionChange(taskGroup.completedField.name, e.target.value)}
                                                options={(taskGroup.completedField.options || []).map(opt => ({ value: opt, label: opt }))}
                                            />
                                        ) : (
                                            <div className="grid grid-cols-2 gap-4">
                                                {(taskGroup.completedField.options || ['Yes', 'No']).map((option, idx) => {
                                                    const isSelected = completedValue === option;
                                                    const baseClasses = "w-full flex items-center justify-center p-4 rounded-lg border-2 font-semibold transition-all duration-200 ease-in-out transform hover:scale-105";
                                                    
                                                    const isNeutral = idx !== 0 && !taskGroup.reasonField;

                                                    const selectedClasses = idx === 0 
                                                        ? 'bg-green-500 border-green-600 text-white shadow-lg' 
                                                        : isNeutral
                                                            ? 'bg-blue-500 border-blue-600 text-white shadow-lg' 
                                                            : 'bg-red-500 border-red-600 text-white shadow-lg';  
                                                    
                                                    const unselectedClasses = 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-600';
                                                    
                                                    return (
                                                    <button
                                                        type="button"
                                                        key={option}
                                                        onClick={() => handleTaskCompletionChange(taskGroup.completedField.name, option)}
                                                        className={`${baseClasses} ${isSelected ? selectedClasses : unselectedClasses}`}
                                                    >
                                                        {isSelected && (idx === 0 || isNeutral ? <CheckCircleIcon className="h-5 w-5 mr-2" /> : <XCircleIcon className="h-5 w-5 mr-2" />)}
                                                        {option}
                                                    </button>
                                                    )
                                                })}
                                            </div>
                                        )}
                                        {formErrors[taskGroup.completedField.name] && <p className="text-red-500 text-xs mt-1">{formErrors[taskGroup.completedField.name]}</p>}
                                    </div>
                                )}

                                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${showReasonField ? 'max-h-96 opacity-100 mt-6' : 'max-h-0 opacity-0'}`}>
                                    {showReasonField && (
                                        <div>
                                            <ModernInput
                                                key={taskGroup.reasonField.id}
                                                textarea
                                                label={taskGroup.reasonField.label}
                                                name={taskGroup.reasonField.name}
                                                value={formData[taskGroup.reasonField.name] || ''}
                                                onChange={handleChange}
                                                placeholder={taskGroup.reasonField.placeholder}
                                                required={taskGroup.reasonField.required}
                                                style={commonInputStyles}
                                                labelStyle={{ color: commonInputStyles.labelColor }}
                                                error={formErrors[taskGroup.reasonField.name]}
                                            />
                                            <div className="mt-4 text-right">
                                                <button
                                                    type="button"
                                                    onClick={handleNextTaskClick} 
                                                    className="inline-flex items-center px-6 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                                                    style={{...buttonStyle, backgroundColor: globalStyles.primaryColor}}
                                                >
                                                    Next
                                                    <ArrowRightCircleIcon className="ml-2 h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )})}
                    </div>
                )}

                {(!hasTasks || allTasksCompleted) && renderFormRows(bottomContentRows)}

                {Object.keys(formErrors).length > 0 && (
                    <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
                        Please correct the following:
                        <ul className="list-disc list-inside mt-2">
                            {Object.values(formErrors).map((msg, idx) => msg && <li key={idx}>{msg}</li>)}
                        </ul>
                    </div>
                )}
                
                {(!hasTasks || allTasksCompleted) && (
                    <div className="w-full">
                        <button type="submit"
                            className="w-full py-4 mt-6 rounded-2xl font-black text-white text-lg shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-blue-500/30 disabled:hover:scale-100 disabled:cursor-not-allowed"
                            style={{
                                ...buttonStyle,
                                opacity: (isSubmitting || isProcessingLocal || isPreview) ? 0.6 : 1
                            }}
                            disabled={isSubmitting || isProcessingLocal || isPreview}
                        >
                            {isSubmitting || isProcessingLocal ? 'Submitting...' : submitButtonText}
                        </button>
                        
                        {/* ⭐ ADDED: Privacy Policy Link Rendered under the button */}
                        {formDefinition?.purpose === 'funnel_form' && formDefinition?.settings?.privacyPolicyUrl && (
                            <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                                By submitting, you agree to our <a href={formDefinition.settings.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline transition-colors">{formDefinition.settings.privacyPolicyText || 'Privacy Policy'}</a>.
                            </p>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
});

export default FormRenderer;